
import React, { useState, useEffect, useRef } from 'react';
import { PropertyType, Property, AgentProfile, MarketStatus, PropertyCategory, Currency } from '../types';
import { X, Sparkles, Loader2, UploadCloud, Trash2, CheckCircle2, Image as ImageIcon, Ruler, MapPin, BadgeDollarSign, Info, Bed, Bath, Car, UtilityPole, Users, Plus, AlertTriangle } from 'lucide-react';
import { generatePropertyDescription } from '../services/geminiService';
import { supabase } from '../supabase';

interface PropertyFormProps {
  onSave: (property: Omit<Property, 'id' | 'agent_id' | 'created_at'>) => Promise<void>;
  onClose: () => void;
  initialData?: Property;
  agent: AgentProfile;
  isSubscribed?: boolean;
}

const MAX_IMAGES = 15;
const DRAFT_KEY = 'findily_property_draft';
const PUBLISH_TIMEOUT = 120000;

const PRESET_FEATURES = [
  'Swimming Pool', 'CCTV', 'Gym', 'Fenced', 'Electricity', 'Security House', 
  'Water Treatment', 'Elevator', 'Cinema Room', 'Boys Quarters'
];

const compressImage = async (file: File): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_WIDTH = 1200;
        const MAX_HEIGHT = 1200;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('Canvas context failed'));
        
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Image compression failed'));
        }, 'image/jpeg', 0.85);
      };
      img.onerror = () => reject(new Error('Failed to load image for compression'));
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
  });
};

export const PropertyForm: React.FC<PropertyFormProps> = ({ onSave, onClose, initialData, agent, isSubscribed }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string>('');
  const [selectedFiles, setSelectedFiles] = useState<{file: File | null, url: string}[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showDraftToast, setShowDraftToast] = useState(false);
  
  const createdUrlsRef = useRef<Set<string>>(new Set());

  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    market_status: initialData?.market_status || 'Available' as MarketStatus,
    category: initialData?.category || 'Sale' as PropertyCategory,
    type: initialData?.type || 'House' as PropertyType,
    subtype: initialData?.subtype || '',
    locality: initialData?.locality || '',
    street_estate: initialData?.street_estate || '',
    bedrooms: initialData?.bedrooms?.toString() || '',
    bathrooms: initialData?.bathrooms?.toString() || '',
    toilets: initialData?.toilets?.toString() || '',
    parking: initialData?.parking?.toString() || '',
    area: initialData?.area?.toString() || '',
    covered_area: initialData?.covered_area?.toString() || '',
    is_furnished: initialData?.is_furnished || false,
    is_serviced: initialData?.is_serviced || false,
    is_shared: initialData?.is_shared || false,
    price: initialData?.price?.toString() || '',
    currency: initialData?.currency || 'NGN' as Currency,
    is_negotiable: initialData?.is_negotiable || false,
    description: initialData?.description || '',
    features: initialData?.features || [] as string[],
    status: initialData?.status || 'Available' as any
  });

  useEffect(() => {
    return () => {
      createdUrlsRef.current.forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      createdUrlsRef.current.clear();
    };
  }, []);

  useEffect(() => {
    if (!initialData) {
      const savedDraft = localStorage.getItem(DRAFT_KEY);
      if (savedDraft) {
        try {
          const parsed = JSON.parse(savedDraft);
          setFormData(parsed);
          setShowDraftToast(true);
        } catch (e) {
          localStorage.removeItem(DRAFT_KEY);
        }
      }
    }
    
    if (initialData?.images) {
      setSelectedFiles(initialData.images.map(url => ({ file: null, url })));
    }
  }, [initialData]);

  useEffect(() => {
    if (!initialData) {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(formData));
    }
  }, [formData, initialData]);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.title) newErrors.title = "Title is required";
    if (!formData.locality) newErrors.locality = "Locality is required";
    if (!formData.price) newErrors.price = "Price is required";
    if (!formData.area) newErrors.area = "Area is required";
    if (selectedFiles.length === 0) newErrors.images = "At least one photo is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAiGenerate = async () => {
    if (!formData.title || !formData.locality) {
      alert("Please enter a title and locality first!");
      return;
    }
    setIsGenerating(true);
    const desc = await generatePropertyDescription({
      title: formData.title,
      type: formData.type,
      location: formData.locality,
      bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : undefined,
      features: formData.features
    });
    setFormData(prev => ({ ...prev, description: desc || '' }));
    setIsGenerating(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []) as File[];
    const remaining = MAX_IMAGES - selectedFiles.length;
    
    const toAdd = files.slice(0, remaining).map((f) => {
      const url = URL.createObjectURL(f);
      createdUrlsRef.current.add(url);
      return { file: f, url };
    });
    
    setSelectedFiles(prev => [...prev, ...toAdd]);
  };

  const removeImage = (index: number) => {
    const item = selectedFiles[index];
    if (item.url.startsWith('blob:')) {
      URL.revokeObjectURL(item.url);
      createdUrlsRef.current.delete(item.url);
    }
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const setAsCover = (index: number) => {
    setSelectedFiles(prev => {
      const copy = [...prev];
      const [item] = copy.splice(index, 1);
      return [item, ...copy];
    });
  };

  const toggleFeature = (feature: string) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature]
    }));
  };

  const formatPriceForDisplay = (val: string) => {
    if (!val) return '';
    const num = val.replace(/\D/g, '');
    if (!num) return '';
    return new Intl.NumberFormat('en-US').format(Number(num));
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, '');
    setFormData({...formData, price: rawValue});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isUploading) return; 
    if (!validate()) return;
    if (!supabase) return alert("Database connection unavailable.");

    setIsUploading(true);
    setUploadProgress('Preparing assets...');

    try {
      const uploadTasks = selectedFiles.map(async (item, idx) => {
        try {
          if (item.file) {
            const compressed = await compressImage(item.file);
            const fileExt = 'jpg';
            const fileName = `${agent.id}/${Date.now()}_${idx}_${Math.random().toString(36).substring(2, 7)}.${fileExt}`;
            
            const { error: uploadError } = await supabase!.storage
              .from('property-images')
              .upload(fileName, compressed, {
                cacheControl: '3600',
                upsert: false
              });
              
            if (uploadError) throw new Error(`Photo ${idx + 1} failed: ${uploadError.message}`);
            
            const { data: { publicUrl } } = supabase!.storage
              .from('property-images')
              .getPublicUrl(fileName);
              
            return publicUrl;
          }
          return item.url;
        } catch (err: any) {
          throw new Error(`Media Error: ${err.message}`);
        }
      });

      setUploadProgress(`Uploading ${selectedFiles.filter(f => f.file).length} photos...`);
      const finalImageUrls = await Promise.all(uploadTasks);

      setUploadProgress('Saving to Findily database...');

      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Connection timed out. Please try again.')), PUBLISH_TIMEOUT)
      );

      const savePromise = onSave({
        ...formData,
        price: Number(formData.price),
        area: Number(formData.area),
        covered_area: formData.covered_area ? Number(formData.covered_area) : undefined,
        bedrooms: formData.bedrooms ? Number(formData.bedrooms) : undefined,
        bathrooms: formData.bathrooms ? Number(formData.bathrooms) : undefined,
        toilets: formData.toilets ? Number(formData.toilets) : undefined,
        parking: formData.parking ? Number(formData.parking) : undefined,
        images: finalImageUrls,
        location: formData.locality,
        features: formData.features,
      });

      await Promise.race([savePromise, timeoutPromise]);
      
      localStorage.removeItem(DRAFT_KEY);
      createdUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
      createdUrlsRef.current.clear();
      onClose();
    } catch (error: any) {
      console.error("Publishing error:", error);
      // If it's a database error from onSave, it's already toasted in App.tsx
      // but we alert here for visibility as requested
      alert(`Publishing Failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsUploading(false);
      setUploadProgress('');
    }
  };

  const isLand = formData.type === 'Land';

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/80 backdrop-blur-xl animate-in fade-in duration-300">
      <div className="bg-white dark:bg-zinc-950 w-full max-w-3xl rounded-[32px] overflow-hidden shadow-2xl relative flex flex-col max-h-[95vh]">
        <div className="p-6 border-b border-gray-100 dark:border-zinc-900 flex justify-between items-center bg-white/80 dark:bg-zinc-950/80 backdrop-blur-md sticky top-0 z-20">
          <div>
            <h2 className="text-xl font-black text-gray-900 dark:text-white tracking-tight uppercase">
              {initialData ? 'Edit Property' : 'Add Property'}
            </h2>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                {isSubscribed ? 'Public Listing Mode' : 'Private Archive Mode'}
              </span>
            </div>
          </div>
          <button onClick={onClose} disabled={isUploading} className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-900 rounded-full transition-colors">
            <X className="w-6 h-6 text-gray-400" />
          </button>
        </div>

        {showDraftToast && (
          <div className="px-6 py-2 bg-blue-500 text-white text-[10px] font-black uppercase tracking-widest flex items-center justify-between">
            <span>Restored draft from last session</span>
            <button onClick={() => setShowDraftToast(false)} className="underline">Dismiss</button>
          </div>
        )}

        <form id="property-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto pb-8 space-y-12 scrollbar-hide">
          {!isSubscribed && (
            <div className="mx-6 mt-6 p-4 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 rounded-2xl flex items-start gap-3 animate-in slide-in-from-top duration-500">
              <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-[11px] font-black text-red-600 dark:text-red-400 uppercase tracking-wider">Account Inactive</p>
                <p className="text-[10px] text-red-500/80 font-bold leading-relaxed">
                  Notice: You don't have an active subscription. This property will be saved to your **Private Archive** and won't be visible to buyers until you subscribe.
                </p>
              </div>
            </div>
          )}

          <div className="px-6 md:px-8 space-y-6">
            <div className="flex items-center gap-2 border-b border-gray-100 dark:border-zinc-900 pb-2">
              <Info className="w-4 h-4 text-gray-400" />
              <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-gray-900 dark:text-white">Section 1: Basic Info</h3>
            </div>
            <div className="space-y-5">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider ml-1">Title *</label>
                <input required className={`w-full px-5 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl outline-none font-bold text-sm border ${errors.title ? 'border-red-500' : 'border-transparent'}`} value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} placeholder="e.g. Luxury 4 Bedroom Semi-Detached" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider ml-1">Category</label>
                  <select className="w-full px-5 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl outline-none font-bold text-sm border border-transparent appearance-none cursor-pointer" value={formData.category} onChange={e => setFormData({...formData, category: e.target.value as PropertyCategory})}>
                    <option value="">Select category</option>
                    <option value="Sale">For Sale</option>
                    <option value="Rent">For Rent</option>
                    <option value="Short Let">Short Let</option>
                    <option value="JV">JV</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider ml-1">Type</label>
                  <select className="w-full px-5 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl outline-none font-bold text-sm border border-transparent appearance-none cursor-pointer" value={formData.type} onChange={e => setFormData({...formData, type: e.target.value as PropertyType})}>
                    <option value="">Select type</option>
                    <option value="House">House</option>
                    <option value="Flat">Flat</option>
                    <option value="Apartment">Apartment</option>
                    <option value="Villa">Villa</option>
                    <option value="Commercial">Commercial</option>
                    <option value="Land">Land</option>
                    <option value="Office">Office</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider ml-1">Subtype (Optional)</label>
                  <input className="w-full px-5 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl outline-none font-bold text-sm border border-transparent" value={formData.subtype} onChange={e => setFormData({...formData, subtype: e.target.value})} placeholder="e.g. Duplex, Penthouse" />
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 md:px-8 space-y-6">
            <div className="flex items-center gap-2 border-b border-gray-100 dark:border-zinc-900 pb-2">
              <MapPin className="w-4 h-4 text-gray-400" />
              <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-gray-900 dark:text-white">Section 2: Location</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider ml-1">Locality *</label>
                <input required className="w-full px-5 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl outline-none font-bold text-sm border border-transparent" value={formData.locality} onChange={e => setFormData({...formData, locality: e.target.value})} placeholder="e.g. Lekki Phase 1, Lagos" />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider ml-1">Street / Estate</label>
                <input className="w-full px-5 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl outline-none font-bold text-sm border border-transparent" value={formData.street_estate} onChange={e => setFormData({...formData, street_estate: e.target.value})} placeholder="e.g. Richmond Gate Estate" />
              </div>
            </div>
          </div>

          <div className="px-6 md:px-8 space-y-6">
            <div className="flex items-center gap-2 border-b border-gray-100 dark:border-zinc-900 pb-2">
              <Ruler className="w-4 h-4 text-gray-400" />
              <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-gray-900 dark:text-white">Section 3: Specifications</h3>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {!isLand && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider ml-1 flex items-center gap-1.5"><Bed className="w-3 h-3" /> Beds</label>
                    <input type="number" className="w-full px-5 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl outline-none font-bold text-sm border border-transparent" value={formData.bedrooms} onChange={e => setFormData({...formData, bedrooms: e.target.value})} placeholder="0" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider ml-1 flex items-center gap-1.5"><Bath className="w-3 h-3" /> Baths</label>
                    <input type="number" className="w-full px-5 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl outline-none font-bold text-sm border border-transparent" value={formData.bathrooms} onChange={e => setFormData({...formData, bathrooms: e.target.value})} placeholder="0" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider ml-1 flex items-center gap-1.5"><UtilityPole className="w-3 h-3" /> Toilets</label>
                    <input type="number" className="w-full px-5 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl outline-none font-bold text-sm border border-transparent" value={formData.toilets} onChange={e => setFormData({...formData, toilets: e.target.value})} placeholder="0" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider ml-1 flex items-center gap-1.5"><Car className="w-3 h-3" /> Parking</label>
                    <input type="number" className="w-full px-5 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl outline-none font-bold text-sm border border-transparent" value={formData.parking} onChange={e => setFormData({...formData, parking: e.target.value})} placeholder="0" />
                  </div>
                </>
              )}
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider ml-1">Area (sqm) *</label>
                <input required type="number" className={`w-full px-5 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl outline-none font-bold text-sm border ${errors.area ? 'border-red-500' : 'border-transparent'}`} value={formData.area} onChange={e => setFormData({...formData, area: e.target.value})} placeholder="0" />
              </div>
              {!isLand && (
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider ml-1">Covered (sqm)</label>
                  <input type="number" className="w-full px-5 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl outline-none font-bold text-sm border border-transparent" value={formData.covered_area} onChange={e => setFormData({...formData, covered_area: e.target.value})} placeholder="0" />
                </div>
              )}
            </div>

            {!isLand && (
              <div className="flex flex-wrap gap-3">
                {[
                  { key: 'is_furnished', label: 'Furnished', icon: Plus },
                  { key: 'is_serviced', label: 'Serviced', icon: Plus },
                  { key: 'is_shared', label: 'Shared Apt', icon: Users },
                ].map((toggle) => (
                  <button key={toggle.key} type="button" onClick={() => setFormData(prev => ({ ...prev, [toggle.key]: !prev[toggle.key as keyof typeof prev] }))} className={`px-4 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all flex items-center gap-2 ${formData[toggle.key as keyof typeof formData] ? 'bg-blue-600 text-white border-blue-600 shadow-lg' : 'bg-gray-50 dark:bg-zinc-900 text-gray-400 border-transparent hover:border-gray-200 dark:hover:border-zinc-800'}`}>
                    <toggle.icon className="w-3 h-3" /> {toggle.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="px-6 md:px-8 space-y-6">
            <div className="flex items-center gap-2 border-b border-gray-100 dark:border-zinc-900 pb-2">
              <BadgeDollarSign className="w-4 h-4 text-gray-400" />
              <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-gray-900 dark:text-white">Section 4: Pricing</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-end">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider ml-1">Price *</label>
                <div className="relative">
                  <div className="absolute left-0 top-0 bottom-0 flex flex-col z-10">
                    <button type="button" onClick={() => setFormData({...formData, currency: 'NGN'})} className={`flex-1 px-3 text-[10px] font-black rounded-tl-2xl transition-colors ${formData.currency === 'NGN' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-zinc-800 text-gray-400'}`}>₦</button>
                    <button type="button" onClick={() => setFormData({...formData, currency: 'USD'})} className={`flex-1 px-3 text-[10px] font-black rounded-bl-2xl transition-colors ${formData.currency === 'USD' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-zinc-800 text-gray-400'}`}>$</button>
                  </div>
                  <input 
                    required 
                    type="text" 
                    className={`w-full pl-16 pr-5 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl outline-none font-black text-lg border ${errors.price ? 'border-red-500' : 'border-transparent'}`} 
                    value={formatPriceForDisplay(formData.price)} 
                    onChange={handlePriceChange} 
                    placeholder="0.00" 
                  />
                </div>
              </div>
              <button type="button" onClick={() => setFormData({...formData, is_negotiable: !formData.is_negotiable})} className={`w-full py-4 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] border transition-all ${formData.is_negotiable ? 'bg-amber-500 text-white border-amber-500' : 'bg-gray-50 dark:bg-zinc-900 text-gray-400 border-transparent'}`}>Negotiable</button>
            </div>
          </div>

          <div className="px-6 md:px-8 space-y-6">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-zinc-900 pb-2">
              <div className="flex items-center gap-2">
                <ImageIcon className="w-4 h-4 text-gray-400" />
                <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-gray-900 dark:text-white">Section 5: Media ({selectedFiles.length}/{MAX_IMAGES})</h3>
              </div>
            </div>
            
            <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
              {selectedFiles.map((item, idx) => (
                <div className="relative aspect-square rounded-xl overflow-hidden group border border-gray-100 dark:border-zinc-800 shadow-sm" key={idx}>
                  <img src={item.url} className="w-full h-full object-cover" alt="Property" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-1.5">
                    <button type="button" onClick={() => removeImage(idx)} className="self-end p-2 bg-red-500 text-white rounded-lg active:scale-90 transition-all"><Trash2 className="w-3.5 h-3.5" /></button>
                    {idx === 0 ? (
                      <div className="bg-white/95 backdrop-blur-md px-2 py-1 rounded-lg text-[8px] font-black uppercase text-gray-900 flex items-center gap-1 mx-auto mb-1"><CheckCircle2 className="w-3 h-3 text-green-500" /> Cover</div>
                    ) : (
                      <button type="button" onClick={() => setAsCover(idx)} className="bg-white/95 backdrop-blur-md px-2 py-1 rounded-lg text-[8px] font-black uppercase text-gray-900 mx-auto mb-1 active:scale-90 transition-all">Set Main</button>
                    )}
                  </div>
                </div>
              ))}
              {selectedFiles.length < MAX_IMAGES && (
                <label className="aspect-square rounded-xl border-2 border-dashed border-gray-200 dark:border-zinc-800 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-900 hover:border-blue-500 transition-all active:scale-95">
                  <UploadCloud className="w-6 h-6 text-gray-400" />
                  <span className="text-[9px] font-black uppercase text-gray-400 mt-2">Add Photo</span>
                  <input type="file" multiple accept="image/*" className="hidden" onChange={handleFileChange} />
                </label>
              )}
            </div>
            {errors.images && <p className="text-[9px] font-bold text-red-500 uppercase">{errors.images}</p>}
          </div>

          {!isLand && (
            <div className="px-6 md:px-8 space-y-4">
              <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-gray-400">Additional Features</h3>
              <div className="flex flex-wrap gap-2">
                {PRESET_FEATURES.map((feature) => (
                  <button key={feature} type="button" onClick={() => toggleFeature(feature)} className={`px-3 py-2 rounded-lg text-[10px] font-bold uppercase border transition-all ${formData.features.includes(feature) ? 'bg-blue-100 text-blue-600 border-blue-200' : 'bg-gray-50 dark:bg-zinc-900 text-gray-500 border-transparent hover:border-gray-200 dark:hover:border-zinc-800'}`}>
                    {feature}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="px-6 md:px-8 space-y-4">
            <div className="flex justify-between items-center border-b border-gray-100 dark:border-zinc-900 pb-2">
              <label className="text-[11px] font-black uppercase tracking-[0.2em] text-gray-400">Listing Description</label>
              <button type="button" onClick={handleAiGenerate} disabled={isGenerating || isUploading} className="flex items-center gap-1.5 text-[10px] font-black uppercase text-blue-500 active:scale-95 transition-all">
                {isGenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Generate with AI
              </button>
            </div>
            <textarea required rows={5} className="w-full px-5 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-[24px] outline-none font-medium text-sm resize-none border border-transparent focus:border-gray-200 dark:focus:border-zinc-800" value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} placeholder="Describe the property's unique selling points..." />
          </div>
        </form>

        <div className="p-6 border-t border-gray-100 dark:border-zinc-900 bg-white/80 dark:bg-zinc-950/80 backdrop-blur-md">
          {isUploading && (
            <div className="mb-4 flex items-center gap-3 px-4 py-2 bg-blue-50 dark:bg-blue-900/10 rounded-xl">
              <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
              <span className="text-[10px] font-black uppercase text-blue-600 dark:text-blue-400 tracking-widest">{uploadProgress}</span>
            </div>
          )}
          <div className="flex gap-4">
            <button type="button" onClick={onClose} disabled={isUploading} className="flex-1 py-4 text-xs font-black uppercase tracking-[0.2em] text-gray-400 hover:text-gray-900 active:scale-95 transition-all">Discard</button>
            <button type="submit" form="property-form" disabled={isUploading} className={`flex-[2] ${isSubscribed ? 'bg-black dark:bg-white text-white dark:text-black' : 'bg-red-500 text-white'} py-4 rounded-[20px] font-black text-xs uppercase tracking-[0.2em] shadow-2xl flex items-center justify-center gap-3 active:scale-[0.98] transition-all disabled:opacity-50`}>
              {isUploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                initialData ? 'Update Listing' : isSubscribed ? 'Publish Listing' : 'Save as Private Draft'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
